import { type ModuleSettingStructure } from "@client/settings/module";
import { EntityType, type GenericMap, type ModuleConfig, type SettingDefinition } from "@client/types";

import blockDefinitions from "./block/definitions.json";
import blockStructure from "./block/structure.json";
import columnDefinitions from "./column/definitions.json";
import columnStructure from "./column/structure.json";
import containerDefinitions from "./container/definitions.json";
import containerStructure from "./container/structure.json";

import { addVersion } from "@client/lib/settings";
import popupDefinitions from "./popup/definitions.json";
import popupStructure from "./popup/structure.json";
import rowDefinitions from "./row/definitions.json";
import rowStructure from "./row/structure.json";

import { getStructureDefaultValueInner, type SettingsStructure } from "@client/settings/structure";

const getEntityDefaultSettingsInner = (type: EntityType): GenericMap<any> => {
  switch (type) {
    case EntityType.Popup:
      return getStructureDefaultValueInner(
        baseStructure,
        commonDefinitionsAll,
        "upgrade-popup",
        popupStructure,
        popupDefinitions
      );
    case EntityType.Block:
      return getStructureDefaultValueInner(
        baseStructure,
        commonDefinitionsAll,
        "upgrade-block",
        blockStructure,
        blockDefinitions
      );
    case EntityType.Row:
      return getStructureDefaultValueInner(
        baseStructure,
        commonDefinitionsAll,
        "upgrade-row",
        rowStructure,
        rowDefinitions
      );
    case EntityType.Column:
      return getStructureDefaultValueInner(
        baseStructure,
        commonDefinitionsAll,
        "upgrade-column",
        columnStructure,
        columnDefinitions
      );
    case EntityType.Container:
      return getStructureDefaultValueInner(
        baseStructure,
        commonDefinitionsAll,
        "upgrade-container",
        containerStructure,
        containerDefinitions
      );
    default:
      throw new Error(`Unsupported entity type: ${type}`);
  }
};

export const getEntityDefaultSettings = (type: EntityType): GenericMap<any> =>
  addVersion(getEntityDefaultSettingsInner(type));

export const getEntitySettingsStructure = (
  type: EntityType
): {
  structure: SettingsStructure;
  definitions: GenericMap<SettingDefinition>;
} => {
  switch (type) {
    case EntityType.Popup:
      return { structure: popupStructure, definitions: popupDefinitions };
    case EntityType.Block:
      return { structure: blockStructure, definitions: blockDefinitions };
    case EntityType.Row:
      return { structure: rowStructure, definitions: rowDefinitions };
    case EntityType.Column:
      return { structure: columnStructure, definitions: columnDefinitions };
    case EntityType.Container:
      return { structure: containerStructure, definitions: containerDefinitions };
    default:
      throw new Error(`Unsupported entity type: ${type}`);
  }
};

export const getSettingsStructure = (moduleType: string): ModuleSettingStructure => {
  const config: ModuleConfig = require(`./content/${moduleType}/config.json`);

  const items: GenericMap<SettingsStructure> = {};
  if (config.item) {
    items["default"] = config.item;
  }
  if (config.items) {
    Object.keys(config.items).reduce((res, x) => {
      res[x] = config.items![x];
      return res;
    }, items);
  }

  const definitions = require(`./content/${moduleType}/definitions.json`);
  let structure;
  try {
    structure = require(`./content/${moduleType}/structure.json`);
  } catch (e) {
    // doesn't exist
  }
  return {
    definitions: definitions ?? null,
    settingsStructure: config.layout ?? {},
    itemsStructure: items,
    commonStructure: structure
  };
};

export const getModuleDefaultSettings = (type: string) => {
  const data = getSettingsStructure(type);

  return addVersion(
    getStructureDefaultValueInner(
      baseStructure,
      commonDefinitionsAll,
      `upgrade-module-${type}`,
      data.settingsStructure,
      data.definitions,
      data.commonStructure
    )
  );
};

const settingsStructureCache: GenericMap<ModuleSettingStructure> = {};
export const getModuleItemDefaultSettings = (type: string, item: string) => {
  if (!(type in settingsStructureCache)) {
    settingsStructureCache[type] = getSettingsStructure(type);
  }
  const data = settingsStructureCache[type];

  return addVersion(
    getStructureDefaultValueInner(
      baseStructure,
      commonDefinitionsAll,
      `upgrade-module-${type}-${item}`,
      data.itemsStructure![item],
      data.definitions,
      data.commonStructure
    )
  );
};

import mainDefinitions from "./main/definitions.json";
import mainStructure from "./main/structure.json";

export const getMainSettingsStructure = (): {
  structure: SettingsStructure;
  definitions: GenericMap<SettingDefinition>;
} => ({
  definitions: mainDefinitions,
  structure: mainStructure
});

export const getDefaultMainSettings = (): GenericMap<any> => {
  const defaultSettings = getStructureDefaultValueInner(
    baseStructure,
    commonDefinitionsAll,
    "upgrade-main",
    mainStructure,
    mainDefinitions
  );
  return addVersion(defaultSettings);
};

import commonContentDefinitions from "./content/definitions.json";
import commonDefinitions from "./definitions.json";

export const commonDefinitionsAll: GenericMap<SettingDefinition> = {
  ...commonDefinitions,
  ...commonContentDefinitions
};

import baseStructure from "./structure.json";
export { baseStructure };
